// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import TokenManager from '../utils/tokenManager';

/**
 * AuthContext — single source of truth for authentication state in React.
 *
 * DESIGN DECISIONS:
 *
 * 1. Value is a single boolean — never put user data, profile, or permissions here.
 *    If you do, every useIsAuthenticated() consumer re-renders on profile changes.
 *    Keep this lean.
 *
 * 2. By the time AuthProvider mounts, tokenRefreshService.initialize() has already
 *    completed (App.tsx awaits it before rendering). This means memory is filled
 *    and authenticated() sync check is correct on first render — no flicker.
 *
 * 3. The useEffect async check is a safety net only — handles edge cases where
 *    initialize() completed but memory wasn't filled (SW unavailable, etc.).
 *
 * 4. Reactive: AuthProvider subscribes to TokenManager, which notifies on every
 *    set()/clear() call — so logging in (or out) within the same page load
 *    updates every useIsAuthenticated() consumer immediately, not just on the
 *    next full page load. (Previously this value was frozen at whatever it
 *    was when AuthProvider first mounted, which meant a guest who logged in
 *    without a page reload stayed "unauthenticated" everywhere that read this
 *    context — header, guards, etc. — until a hard refresh.)
 *
 * 5. Logout path: tokenRefreshService.logout() calls window.location.href = LOGIN.
 *    Hard redirect — page dies. Context update on logout still happens (via the
 *    subscription below) but is moot since the page is about to unload anyway.
 */

const AuthContext = createContext<boolean>(false);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  // Sync check first — if App.tsx awaited initialize() correctly,
  // this already returns the right value and no async update is needed.
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() =>
    TokenManager.authenticated()
  );

  useEffect(() => {
    let mounted = true;

    // Safety net: handles the case where initialize() completed but
    // SW token wasn't cached to memory yet (rare, but possible).
    TokenManager.hasToken().then((has) => {
      if (mounted && has !== isAuthenticated) {
        setIsAuthenticated(has);
      }
    });

    // Stay in sync with every subsequent set()/clear() — login, MFA
    // success, logout, token refresh, etc. — for the rest of this page's
    // lifetime, not just the initial mount.
    const unsubscribe = TokenManager.subscribe((has) => {
      if (mounted) {
        setIsAuthenticated(has);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  return (
    <AuthContext.Provider value={isAuthenticated}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useIsAuthenticated — reactive auth state for components that render
 * before memory is guaranteed to be filled (public pages, DashboardLayout,
 * Header on public routes).
 *
 * DO NOT use this behind PrivateRoute — authenticated() is sufficient there
 * because memory is filled before any child mounts.
 */
export const useIsAuthenticated = (): boolean => {
  return useContext(AuthContext);
};
